import { Button, Form, Input, message, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signUp } from "../api/allauth";
import { ROUTES } from "../routes";
import { useAuth, useConfig } from "./AuthContext";
import ProviderList from "./ProviderList";

import styles from "./Accounts.module.css";

const { Title, Text, Link } = Typography;

const SignupPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/";

    const { auth } = useAuth();
    const config = useConfig();
    const hasProviders = config?.data?.socialaccount?.providers?.length > 0;

    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    useEffect(() => {
        if (auth?.meta?.is_authenticated) {
            navigate(from, { replace: true });
        }
    }, [auth, from, navigate]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const content = await signUp({
                email: values.email.trim(),
                password: password,
            });

            if (content.status === 200) {
                message.success("Signup successful! Please check your email.");
                navigate(ROUTES.ACCOUNT.LOGIN);
            } else if (content.status === 400) {
                message.error(content.errors[0].message);
            } else {
                message.error("Unexpected signup response");
            }
        } catch (err) {
            console.error(err);
            message.error("Signup failed, please try again");
        } finally {
            setLoading(false);
        }
    };

    if (auth === undefined) return <Spin tip="Loading..." />;

    // Common passwords for basic frontend validation
    const commonPasswords = ["123456", "password", "12345678", "qwerty", "abc123"];

    return (
        <div className={styles.card}>
            {/* Logo */}
            <img src="/w5n-logo.svg" alt="Logo" className={styles.logo} />
            <Title level={2} className={styles.title}>Create your account</Title>
            <Text className={styles.subtitle}>
                Already have an account?{" "}
                <Link onClick={() => navigate(ROUTES.ACCOUNT.LOGIN)}>Log in</Link>
            </Text>

            <Form layout="vertical" onFinish={handleSubmit}>
                {/* Email */}
                <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                        { required: true, message: "Please enter your email" },
                        { type: "email", message: "Please enter a valid email" },
                    ]}
                >
                    <Input
                        placeholder="Enter your email address"
                        size="large"
                        autoComplete="username"
                    />
                </Form.Item>

                {/* Password */}
                <Form.Item label="Password" required>
                    <Form.Item
                        name="password"
                        noStyle
                        rules={[
                            { required: true, message: "Please enter a password" },
                            { min: 8, message: "Password must be at least 8 characters" },
                            {
                                validator: (_, value) => {
                                    if (!value || !commonPasswords.includes(value.toLowerCase())) return Promise.resolve();
                                    return Promise.reject(new Error("This password is too common."));
                                },
                            },
                            {
                                validator: (_, value) => {
                                    if (!value || /\D/.test(value)) return Promise.resolve();
                                    return Promise.reject(new Error("Password cannot be entirely numeric."));
                                },
                            },
                            ({ getFieldValue }) => ({
                                validator: (_, value) => {
                                    const email = getFieldValue("email") || "";
                                    if (!value || !email || !value.toLowerCase().includes(email.split("@")[0].toLowerCase())) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error("Password is too similar to your email."));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            placeholder="Create a password"
                            size="large"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="new-password"
                        />
                    </Form.Item>
                    <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                        At least 8 characters, not numeric only, not too common, and not similar to your email.
                    </div>
                </Form.Item>

                {/* Confirm Password */}
                <Form.Item label="Confirm Password" required>
                    <Form.Item
                        name="confirmPassword"
                        noStyle
                        dependencies={["password"]}
                        rules={[
                            { required: true, message: "Please confirm your password" },
                            {
                                validator: (_, value) => {
                                    if (!value || value === password) return Promise.resolve();
                                    return Promise.reject(new Error("Passwords do not match!"));
                                },
                            },
                        ]}
                    >
                        <Input.Password
                            placeholder="Repeat your password"
                            size="large"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                        />
                    </Form.Item>
                </Form.Item>

                {/* Submit */}
                <div style={{ marginTop: 30 }}>
                    <Form.Item>
                        <Button htmlType="submit" loading={loading} block size="large">
                            Sign Up
                        </Button>
                    </Form.Item>
                </div>
            </Form>

            {hasProviders && <ProviderList callbackURL="/account/provider/callback" />}
        </div>
    );
};

export default SignupPage;
